import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  KeyRound,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Plus,
  Trash2,
  Edit3,
  Copy,
  Check,
  RefreshCw,
  X,
  Calendar,
  Users,
  User,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  Search,
  CheckSquare,
  Square,
  ArrowRight,
  Clock,
  Layers,
  Lock
} from "lucide-react";

export interface GlobalLoginKey {
  id: string;
  name: string;
  secretKey: string;
  expiry: string;
  syncExpiryToUsers: boolean;
  applicableScope: "all" | "groups" | "users";
  assignedGroups: string[];
  assignedUsers: string[];
  enabled: boolean;
  description?: string;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  coveredUserCount?: number;
  isExpired?: boolean;
  canEdit?: boolean;
}

interface GlobalLoginKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  systemUsers: any[];
  onRefreshUsers: () => void;
  onToast?: (msg: string, type?: "success" | "error" | "info") => void;
}

export const GlobalLoginKeyModal: React.FC<GlobalLoginKeyModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  systemUsers,
  onRefreshUsers,
  onToast
}) => {
  const [keys, setKeys] = useState<GlobalLoginKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Editor mode: "list" | "create" | "edit"
  const [viewMode, setViewMode] = useState<"list" | "create" | "edit">("list");
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null);

  // Form states
  const [formName, setFormName] = useState("");
  const [formSecretKey, setFormSecretKey] = useState("");
  const [formExpiry, setFormExpiry] = useState("");
  const [formSyncExpiry, setFormSyncExpiry] = useState(true);
  const [formScope, setFormScope] = useState<"all" | "groups" | "users">("groups");
  const [formAssignedGroups, setFormAssignedGroups] = useState<string[]>([]);
  const [formAssignedUsers, setFormAssignedUsers] = useState<string[]>([]);
  const [formEnabled, setFormEnabled] = useState(true);
  const [formDescription, setFormDescription] = useState("");

  // UI helpers
  const [showSecretMap, setShowSecretMap] = useState<Record<string, boolean>>({});
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [deleteConfirmKeyId, setDeleteConfirmKeyId] = useState<string | null>(null);

  const isSuperAdmin = currentUser?.role === "admin";
  const myGroup = currentUser?.group || "默认分组";

  // All unique groups in system
  const availableGroups = useMemo(() => {
    const set = new Set<string>();
    set.add(myGroup);
    set.add("默认分组");
    systemUsers.forEach((u) => {
      if (u.group) set.add(u.group);
    });
    return Array.from(set);
  }, [systemUsers, myGroup]);

  // Helper to safely retrieve user authentication token from storage
  const getAuthToken = () => {
    return (
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token") ||
      localStorage.getItem("token") ||
      (currentUser as any)?.token ||
      ""
    );
  };

  // Load keys from server
  const fetchKeys = async () => {
    try {
      setLoading(true);
      setError("");
      const token = getAuthToken();
      const res = await fetch("/api/auth/global-login-keys", {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "获取全局登录校验密钥失败");
      }
      setKeys(data.keys || []);
    } catch (err: any) {
      setError(err.message || "网络请求异常");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchKeys();
      setViewMode("list");
      setError("");
      setSuccessMsg("");
    }
  }, [isOpen]);

  // Generate random 10-char alphanumeric key strictly consistent with original system format (e.g. oWxKLRppnU)
  const handleGenerateSecret = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let gen = "";
    for (let i = 0; i < 10; i++) {
      gen += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormSecretKey(gen);
  };

  // Quick expiry offsets
  const handleSetQuickExpiry = (days: number | null) => {
    if (days === null) {
      setFormExpiry("");
      return;
    }
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(23, 59, 0, 0);
    const pad = (n: number) => n.toString().padStart(2, "0");
    const formatted = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setFormExpiry(formatted);
  };

  // Open Create View
  const handleOpenCreate = () => {
    setViewMode("create");
    setEditingKeyId(null);
    setFormName("");
    handleGenerateSecret();
    handleSetQuickExpiry(30);
    setFormSyncExpiry(true);
    setFormScope(isSuperAdmin ? "all" : "groups");
    setFormAssignedGroups([myGroup]);
    setFormAssignedUsers([]);
    setFormEnabled(true);
    setFormDescription("");
    setError("");
    setSuccessMsg("");
  };

  // Open Edit View
  const handleOpenEdit = (key: GlobalLoginKey) => {
    setViewMode("edit");
    setEditingKeyId(key.id);
    setFormName(key.name || "");
    setFormSecretKey(key.secretKey || "");
    setFormExpiry(key.expiry || "");
    setFormSyncExpiry(key.syncExpiryToUsers !== false);
    setFormScope(key.applicableScope || "all");
    setFormAssignedGroups(Array.isArray(key.assignedGroups) ? [...key.assignedGroups] : []);
    setFormAssignedUsers(Array.isArray(key.assignedUsers) ? [...key.assignedUsers] : []);
    setFormEnabled(key.enabled !== false);
    setFormDescription(key.description || "");
    setError("");
    setSuccessMsg("");
  };

  // Save Key (Create or Update)
  const handleSaveKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      setError("请输入密钥名称");
      return;
    }
    if (!formSecretKey.trim()) {
      setError("请输入或生成校验密钥");
      return;
    }

    if (formScope === "groups" && formAssignedGroups.length === 0) {
      setError("请至少勾选一个目标分配用户组");
      return;
    }

    if (formScope === "users" && formAssignedUsers.length === 0) {
      setError("请至少指定一个目标分配账户");
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setSuccessMsg("");
      const token = getAuthToken();

      const payload = {
        id: editingKeyId,
        name: formName.trim(),
        secretKey: formSecretKey.trim(),
        expiry: formExpiry.trim(),
        syncExpiryToUsers: formSyncExpiry,
        applicableScope: formScope,
        assignedGroups: formAssignedGroups,
        assignedUsers: formAssignedUsers,
        enabled: formEnabled,
        description: formDescription.trim()
      };

      const url = viewMode === "create" ? "/api/auth/global-login-keys" : "/api/auth/global-login-keys/update";
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "保存失败");
      }

      const count = data.syncedUsersCount || 0;
      const tip = count > 0 
        ? `保存成功！已自动联动同步至 ${count} 个关联账户。` 
        : "保存成功！";
      setSuccessMsg(tip);
      if (onToast) onToast(tip, "success");

      await fetchKeys();
      onRefreshUsers();
      setViewMode("list");
    } catch (err: any) {
      setError(err.message || "操作异常");
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle Enable status
  const handleToggleEnabled = async (key: GlobalLoginKey) => {
    try {
      const token = getAuthToken();
      const res = await fetch("/api/auth/global-login-keys/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          id: key.id,
          enabled: !key.enabled
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "切换状态失败");
      }
      setKeys((prev) =>
        prev.map((k) => (k.id === key.id ? { ...k, enabled: !k.enabled } : k))
      );
      onRefreshUsers();
      if (onToast) {
        onToast(`密钥【${key.name}】已${!key.enabled ? "启用" : "停用"}`, "info");
      }
    } catch (err: any) {
      if (onToast) onToast(err.message || "切换失败", "error");
    }
  };

  // Delete Key
  const handleDeleteKey = async (id: string) => {
    try {
      setActionLoading(true);
      const token = getAuthToken();
      const res = await fetch("/api/auth/global-login-keys/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ id })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "删除失败");
      }
      setDeleteConfirmKeyId(null);
      await fetchKeys();
      onRefreshUsers();
      if (onToast) onToast("全局校验密钥已成功删除", "success");
    } catch (err: any) {
      setError(err.message || "删除异常");
    } finally {
      setActionLoading(false);
    }
  };

  // Force Sync Key to Users
  const handleForceSyncToUsers = async (key: GlobalLoginKey) => {
    try {
      setActionLoading(true);
      const token = getAuthToken();
      const res = await fetch("/api/auth/global-login-keys/sync-to-users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ id: key.id })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "同步失败");
      }
      const count = data.updatedCount || 0;
      const msg = `已成功将密钥【${key.name}】及有效期强制同步给 ${count} 个账户！`;
      setSuccessMsg(msg);
      if (onToast) onToast(msg, "success");
      onRefreshUsers();
    } catch (err: any) {
      setError(err.message || "同步异常");
    } finally {
      setActionLoading(false);
    }
  };

  // Copy secret key
  const handleCopy = (secret: string, keyId: string) => {
    navigator.clipboard.writeText(secret);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  // Filtered users for specific user assignment
  const filteredUsers = useMemo(() => {
    let list = systemUsers;
    if (!isSuperAdmin) {
      list = list.filter((u) => (u.group || "默认分组") === myGroup);
    }
    if (!userSearchQuery.trim()) return list;
    const q = userSearchQuery.trim().toLowerCase();
    return list.filter(
      (u) =>
        (u.username || "").toLowerCase().includes(q) ||
        (u.group || "").toLowerCase().includes(q)
    );
  }, [systemUsers, isSuperAdmin, myGroup, userSearchQuery]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl shadow-black/80 overflow-hidden text-slate-100 font-sans">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/80 bg-slate-900/90 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 text-amber-400 shadow-inner">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white tracking-wide">
                  全局登录校验密钥配置中心
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/10 text-amber-300 border border-amber-500/20">
                  {keys.filter((k) => k.enabled).length} 个启用中
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                密钥与账户有效期深度联动，可分配至全局所有账户、指定管理组或指定账户
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {viewMode === "list" && (
              <>
                <button
                  type="button"
                  onClick={fetchKeys}
                  disabled={loading}
                  title="刷新密钥列表"
                  className="p-2 rounded-xl border border-slate-800 bg-slate-800/50 hover:bg-slate-700/60 text-slate-300 hover:text-white transition cursor-pointer"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
                <button
                  type="button"
                  onClick={handleOpenCreate}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white shadow-lg shadow-amber-600/20 transition cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  新建校验密钥
                </button>
              </>
            )}
            {viewMode !== "list" && (
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border border-slate-800 bg-slate-800/60 hover:bg-slate-700 text-slate-300 transition cursor-pointer"
              >
                返回密钥列表
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Global Notifications */}
        {error && (
          <div className="mx-5 mt-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
              <span>{error}</span>
            </div>
            <button
              type="button"
              onClick={() => setError("")}
              className="text-rose-400 hover:text-rose-200 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {successMsg && (
          <div className="mx-5 mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 shrink-0 text-emerald-400" />
              <span>{successMsg}</span>
            </div>
            <button
              type="button"
              onClick={() => setSuccessMsg("")}
              className="text-emerald-400 hover:text-emerald-200 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Body Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {viewMode === "list" ? (
            /* LIST VIEW */
            <>
              {loading && keys.length === 0 ? (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
                  <p className="text-xs">加载全局登录校验密钥中...</p>
                </div>
              ) : keys.length === 0 ? (
                <div className="py-16 text-center rounded-2xl border border-dashed border-slate-800/80 bg-slate-950/40 p-8">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                    <KeyRound className="w-6 h-6" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-200">暂未配置全局登录校验密钥</h4>
                  <p className="text-xs text-slate-400 max-w-md mx-auto mt-1 mb-5">
                    创建全局登录校验密钥后，您可以分配指定账户或组登录时强制校验该密钥，并与账户登录有效期强关联联动。
                  </p>
                  <button
                    type="button"
                    onClick={handleOpenCreate}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition cursor-pointer shadow-lg shadow-amber-600/20"
                  >
                    <Plus className="w-4 h-4" />
                    立即创建首个全局校验密钥
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3.5">
                  {keys.map((k) => {
                    const isVisible = showSecretMap[k.id] || false;
                    const isCopied = copiedKeyId === k.id;
                    const isDeleteConfirm = deleteConfirmKeyId === k.id;

                    return (
                      <div
                        key={k.id}
                        className={`p-4 rounded-xl border transition-all ${
                          k.enabled
                            ? "bg-slate-950/70 border-slate-800 hover:border-slate-700"
                            : "bg-slate-950/30 border-slate-800/40 opacity-70"
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          {/* Left Column Info */}
                          <div className="space-y-2 flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="text-sm font-bold text-white truncate">{k.name}</h4>
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold ${
                                  k.enabled
                                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                    : "bg-slate-800 text-slate-400"
                                }`}
                              >
                                {k.enabled ? "已生效启用" : "已停用"}
                              </span>

                              {/* Scope Badge */}
                              {k.applicableScope === "all" && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-sky-500/10 text-sky-300 border border-sky-500/20 flex items-center gap-1">
                                  <Users className="w-3 h-3" /> 全局所有账户 ({k.coveredUserCount ?? 0})
                                </span>
                              )}
                              {k.applicableScope === "groups" && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 text-purple-300 border border-purple-500/20 flex items-center gap-1">
                                  <Layers className="w-3 h-3" /> 分配至 {k.assignedGroups?.length || 0} 个用户组 ({k.coveredUserCount ?? 0} 人)
                                </span>
                              )}
                              {k.applicableScope === "users" && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 flex items-center gap-1">
                                  <User className="w-3 h-3" /> 分配至 {k.assignedUsers?.length || 0} 个指定账户
                                </span>
                              )}

                              {/* Expiry Status */}
                              {k.isExpired ? (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                  已过期失效
                                </span>
                              ) : k.expiry ? (
                                <span className="px-2 py-0.5 rounded text-[10px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-1">
                                  <Clock className="w-3 h-3 text-emerald-400" />
                                  有效期至: {k.expiry.replace("T", " ")}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium text-slate-400 bg-slate-800/80">
                                  永久有效
                                </span>
                              )}

                              {/* Expiry Linkage Badge */}
                              {k.syncExpiryToUsers && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                                  🔗 与账户有效期强关联
                                </span>
                              )}
                            </div>

                            {/* Secret Display Row */}
                            <div className="flex items-center gap-2 pt-1">
                              <span className="text-xs text-slate-400 font-mono">校验密钥:</span>
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 font-mono text-xs text-amber-200">
                                <Lock className="w-3.5 h-3.5 text-amber-400" />
                                <span>{isVisible ? k.secretKey : "••••••••••••"}</span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowSecretMap((prev) => ({
                                      ...prev,
                                      [k.id]: !prev[k.id]
                                    }))
                                  }
                                  className="ml-1 text-slate-400 hover:text-slate-200 transition cursor-pointer"
                                  title={isVisible ? "隐藏密钥" : "查看密钥"}
                                >
                                  {isVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(k.secretKey, k.id)}
                                  className="ml-1 text-slate-400 hover:text-white transition cursor-pointer"
                                  title="复制密钥"
                                >
                                  {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                              {isCopied && <span className="text-[10px] text-emerald-400 font-sans">已复制</span>}
                            </div>

                            {/* Assigned Detail List */}
                            <div className="text-[11px] text-slate-400 pt-1 flex flex-wrap items-center gap-1.5">
                              <span className="text-slate-500">分配明细:</span>
                              {k.applicableScope === "all" && (
                                <span className="text-slate-300 font-sans">面向系统全体登录账户生效</span>
                              )}
                              {k.applicableScope === "groups" && (
                                <div className="flex flex-wrap gap-1">
                                  {k.assignedGroups?.map((g) => (
                                    <span
                                      key={g}
                                      className="px-1.5 py-0.5 rounded bg-purple-950/40 text-purple-200 border border-purple-800/40 font-mono text-[10px]"
                                    >
                                      组: {g}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {k.applicableScope === "users" && (
                                <div className="flex flex-wrap gap-1">
                                  {k.assignedUsers?.map((u) => (
                                    <span
                                      key={u}
                                      className="px-1.5 py-0.5 rounded bg-indigo-950/40 text-indigo-200 border border-indigo-800/40 font-mono text-[10px]"
                                    >
                                      @{u}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {k.description && (
                              <p className="text-[11px] text-slate-400 italic pt-0.5">{k.description}</p>
                            )}
                          </div>

                          {/* Right Column Actions */}
                          <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 shrink-0 border-t sm:border-t-0 border-slate-800/80 pt-2 sm:pt-0">
                            {/* Enable/Disable Switch */}
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <span className="text-xs text-slate-400 font-sans">
                                {k.enabled ? "已启用" : "已停用"}
                              </span>
                              <input
                                type="checkbox"
                                checked={k.enabled}
                                onChange={() => handleToggleEnabled(k)}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                            </label>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleForceSyncToUsers(k)}
                                disabled={actionLoading}
                                title="立即将该密钥和有效期同步覆盖给关联账户"
                                className="px-2.5 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-[11px] font-medium transition flex items-center gap-1 cursor-pointer"
                              >
                                <RefreshCw className="w-3 h-3" />
                                立即同步至账户
                              </button>

                              <button
                                type="button"
                                onClick={() => handleOpenEdit(k)}
                                title="编辑此密钥配置"
                                className="p-1.5 rounded-lg border border-slate-800 bg-slate-800/60 hover:bg-slate-700 text-slate-300 hover:text-white transition cursor-pointer"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>

                              {isDeleteConfirm ? (
                                <div className="flex items-center gap-1 bg-rose-950/60 border border-rose-600/40 px-2 py-1 rounded-lg">
                                  <span className="text-[10px] text-rose-300">确认删除?</span>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteKey(k.id)}
                                    className="px-1.5 py-0.5 rounded bg-rose-600 text-white text-[10px] hover:bg-rose-500 cursor-pointer font-bold"
                                  >
                                    是
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setDeleteConfirmKeyId(null)}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] hover:bg-slate-700 cursor-pointer"
                                  >
                                    否
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setDeleteConfirmKeyId(k.id)}
                                  title="删除此密钥"
                                  className="p-1.5 rounded-lg border border-slate-800 bg-slate-800/60 hover:bg-rose-900/30 hover:border-rose-700/50 text-slate-400 hover:text-rose-300 transition cursor-pointer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* CREATE / EDIT FORM VIEW */
            <form onSubmit={handleSaveKey} className="space-y-4">
              <div className="p-4 rounded-xl border border-slate-800 bg-slate-950/50 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-2.5">
                  <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {viewMode === "create" ? "新建全局登录校验密钥" : "编辑全局登录校验密钥"}
                  </h4>
                  <span className="text-[10px] text-slate-500 font-sans">
                    填写密钥信息，设置有效期联动，并分配指定账户或组
                  </span>
                </div>

                {/* Name & Key String */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      密钥名称 / 标识备注 <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="例如: 运营一部季度卡密、VIP全功能授权密钥"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-amber-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center justify-between">
                      <span>登录校验密钥 (Secret Key) <span className="text-rose-400">*</span></span>
                      <button
                        type="button"
                        onClick={handleGenerateSecret}
                        className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1 cursor-pointer font-sans"
                      >
                        <Sparkles className="w-3 h-3" />
                        随机生成
                      </button>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        maxLength={10}
                        value={formSecretKey}
                        onChange={(e) => setFormSecretKey(e.target.value.substring(0, 10))}
                        placeholder="例如: aB9xKd3f92 (与原系统一致为10位字符)"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-amber-200 placeholder-slate-600 font-mono focus:outline-none focus:border-amber-500"
                        required
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1 font-sans">
                      与原系统一致为10位字符（大小写字母与数字组合，如 oWxKLRppnU），操作员需该密钥配合账号进行系统登录校验。
                    </p>
                  </div>
                </div>

                {/* Expiry and Account Expiry Linkage (CRITICAL SECTION) */}
                <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-900 to-amber-950/20 border border-amber-500/30 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <label className="block text-xs font-bold text-slate-200 flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-amber-400" />
                        密钥有效期设置 (Key Expiry)
                      </label>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        设置此密钥的有效截止时间。超过此时间，即便输入该密钥也将被系统拒绝登录。
                      </p>
                    </div>

                    <div className="text-right">
                      <span className="text-[11px] font-mono text-amber-300 font-semibold">
                        {formExpiry ? `截止时间: ${formExpiry.replace("T", " ")}` : "当前设置: 永久有效"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-2">
                    <input
                      type="datetime-local"
                      step="1"
                      value={formExpiry}
                      onChange={(e) => setFormExpiry(e.target.value)}
                      className="w-full sm:w-auto flex-1 px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-amber-500 font-mono"
                    />

                    <div className="flex flex-wrap items-center gap-1">
                      {[7, 30, 90, 180, 365].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => handleSetQuickExpiry(d)}
                          className="px-2.5 py-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 text-[11px] text-slate-300 hover:text-white transition cursor-pointer"
                        >
                          +{d}天
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleSetQuickExpiry(null)}
                        className="px-2.5 py-1.5 rounded-lg border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-[11px] text-rose-300 transition cursor-pointer"
                      >
                        永久有效
                      </button>
                    </div>
                  </div>

                  {/* DEEP LINKAGE WITH ACCOUNT VALIDITY */}
                  <div className="pt-2 border-t border-slate-800/80">
                    <label className="flex items-start gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formSyncExpiry}
                        onChange={(e) => setFormSyncExpiry(e.target.checked)}
                        className="mt-0.5 rounded border-slate-700 bg-slate-900 text-amber-500 focus:ring-amber-500 cursor-pointer"
                      />
                      <div className="space-y-0.5">
                        <span className="text-xs font-bold text-amber-300 font-sans flex items-center gap-1.5">
                          <span>与账户有效期强关联联动（自动同步到关联账户）</span>
                          <span className="px-1.5 py-0.2 rounded text-[9px] bg-amber-500/20 text-amber-200 border border-amber-400/30 font-mono">
                            推荐开启
                          </span>
                        </span>
                        <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
                          开启后，保存或用户以此密钥登录时，系统将自动把分配范围内账户的生命周期与本密钥的有效期强关联同步。密钥续期或到期时，关联的所有账户将同步协同生效或失效，实现账号生命周期一体化集中管控。
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Assignment Target Scope */}
                <div className="space-y-3">
                  <label className="block text-xs font-bold text-slate-200">
                    分配指定账户或组 (适用授权范围) <span className="text-rose-400">*</span>
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {isSuperAdmin && (
                      <label
                        className={`p-3 rounded-xl border flex items-center gap-2.5 cursor-pointer transition ${
                          formScope === "all"
                            ? "bg-sky-950/40 border-sky-500 text-sky-200 shadow-md shadow-sky-900/20"
                            : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                        }`}
                      >
                        <input
                          type="radio"
                          name="scope"
                          value="all"
                          checked={formScope === "all"}
                          onChange={() => setFormScope("all")}
                          className="sr-only"
                        />
                        <Users className="w-4 h-4 text-sky-400" />
                        <div>
                          <p className="text-xs font-bold font-sans">🌐 全局全部账户</p>
                          <p className="text-[10px] text-slate-500">全系统所有人员通用强制</p>
                        </div>
                      </label>
                    )}

                    <label
                      className={`p-3 rounded-xl border flex items-center gap-2.5 cursor-pointer transition ${
                        formScope === "groups"
                          ? "bg-purple-950/40 border-purple-500 text-purple-200 shadow-md shadow-purple-900/20"
                          : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="scope"
                        value="groups"
                        checked={formScope === "groups"}
                        onChange={() => setFormScope("groups")}
                        className="sr-only"
                      />
                      <Layers className="w-4 h-4 text-purple-400" />
                      <div>
                        <p className="text-xs font-bold font-sans">👥 分配至指定用户组</p>
                        <p className="text-[10px] text-slate-500">按部门/分组统一批量管控</p>
                      </div>
                    </label>

                    <label
                      className={`p-3 rounded-xl border flex items-center gap-2.5 cursor-pointer transition ${
                        formScope === "users"
                          ? "bg-indigo-950/40 border-indigo-500 text-indigo-200 shadow-md shadow-indigo-900/20"
                          : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="scope"
                        value="users"
                        checked={formScope === "users"}
                        onChange={() => setFormScope("users")}
                        className="sr-only"
                      />
                      <User className="w-4 h-4 text-indigo-400" />
                      <div>
                        <p className="text-xs font-bold font-sans">👤 分配至指定账户</p>
                        <p className="text-[10px] text-slate-500">挑选具体操作员账号单独分配</p>
                      </div>
                    </label>
                  </div>

                  {/* Scope Detailed Selectors */}
                  {formScope === "groups" && (
                    <div className="p-3.5 rounded-xl bg-slate-900 border border-purple-500/30 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-purple-300">
                          请勾选要分配此校验密钥的用户组:
                        </span>
                        <div className="flex gap-2 text-[11px]">
                          <button
                            type="button"
                            onClick={() => setFormAssignedGroups([...availableGroups])}
                            className="text-sky-400 hover:underline cursor-pointer"
                          >
                            全选组
                          </button>
                          <span className="text-slate-600">|</span>
                          <button
                            type="button"
                            onClick={() => setFormAssignedGroups([])}
                            className="text-slate-400 hover:underline cursor-pointer"
                          >
                            清空
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                        {availableGroups.map((grp) => {
                          const isChecked = formAssignedGroups.includes(grp);
                          return (
                            <label
                              key={grp}
                              className={`p-2.5 rounded-xl border flex items-center gap-2 cursor-pointer transition text-xs font-medium ${
                                isChecked
                                  ? "bg-purple-900/30 border-purple-500 text-white font-semibold"
                                  : "bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormAssignedGroups([...formAssignedGroups, grp]);
                                  } else {
                                    setFormAssignedGroups(formAssignedGroups.filter((g) => g !== grp));
                                  }
                                }}
                                className="sr-only"
                              />
                              {isChecked ? (
                                <CheckSquare className="w-4 h-4 text-purple-400 shrink-0" />
                              ) : (
                                <Square className="w-4 h-4 text-slate-600 shrink-0" />
                              )}
                              <span className="truncate">{grp}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {formScope === "users" && (
                    <div className="p-3.5 rounded-xl bg-slate-900 border border-indigo-500/30 space-y-2.5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-indigo-300">
                          请勾选要分配此校验密钥的账户 (已选择: {formAssignedUsers.length} 人):
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2 pointer-events-none" />
                            <input
                              type="text"
                              value={userSearchQuery}
                              onChange={(e) => setUserSearchQuery(e.target.value)}
                              placeholder="搜索用户名/分组..."
                              className="pl-8 pr-3 py-1 text-xs rounded-lg bg-slate-950 border border-slate-800 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setFormAssignedUsers(filteredUsers.map((u) => u.username.toLowerCase()))
                            }
                            className="text-[11px] text-sky-400 hover:underline cursor-pointer"
                          >
                            全选
                          </button>
                          <span className="text-slate-600 text-xs">|</span>
                          <button
                            type="button"
                            onClick={() => setFormAssignedUsers([])}
                            className="text-[11px] text-slate-400 hover:underline cursor-pointer"
                          >
                            清空
                          </button>
                        </div>
                      </div>

                      <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                        {filteredUsers.map((u) => {
                          const uname = (u.username || "").toLowerCase();
                          const isChecked = formAssignedUsers.includes(uname);
                          return (
                            <label
                              key={uname}
                              className={`p-2 rounded-lg border flex items-center justify-between gap-2 cursor-pointer transition text-xs ${
                                isChecked
                                  ? "bg-indigo-950/50 border-indigo-500/60 text-white font-medium"
                                  : "bg-slate-950/60 border-slate-800/80 text-slate-400 hover:text-slate-200"
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormAssignedUsers([...formAssignedUsers, uname]);
                                    } else {
                                      setFormAssignedUsers(formAssignedUsers.filter((x) => x !== uname));
                                    }
                                  }}
                                  className="sr-only"
                                />
                                {isChecked ? (
                                  <CheckSquare className="w-4 h-4 text-indigo-400 shrink-0" />
                                ) : (
                                  <Square className="w-4 h-4 text-slate-600 shrink-0" />
                                )}
                                <span className="font-mono text-xs">{u.username}</span>
                                <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-800 text-slate-400">
                                  {u.role === "admin" ? "超级管理员" : u.role === "manager" ? "管理员" : "操作员"}
                                </span>
                              </div>
                              <span className="text-[10px] font-mono text-purple-300 shrink-0">
                                {u.group || "默认分组"}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Enabled Toggle & Description */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      补充说明 / 备注信息 (可选)
                    </label>
                    <input
                      type="text"
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="例如: 2026年第二季度客服一部统一卡密"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800">
                    <div>
                      <p className="text-xs font-bold text-slate-200">启用状态</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {formEnabled ? "保存后立即生效执行登录校验" : "暂时保存，暂不生效"}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formEnabled}
                        onChange={(e) => setFormEnabled(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className="px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs font-medium transition cursor-pointer"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white text-xs font-semibold shadow-lg shadow-amber-600/25 transition cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      保存并同步中...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      保存并应用生效
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
